package components.auth

import androidx.compose.runtime.*
import com.example.readingnotes.presentation.auth.AuthEvent
import com.example.readingnotes.presentation.auth.AuthViewModel
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import design.DesignTokens

/**
 * Registration Step 2: Email Verification
 * Enter 6-digit code and verify email
 */
@Composable
fun RegistrationStep2_Verify(
    email: String,
    code: String,
    onCodeChange: (String) -> Unit,
    onVerify: () -> Unit,
    onResend: () -> Unit,
    onChangeEmail: () -> Unit,
    isLoading: Boolean,
    error: String?,
    onSendCode: () -> Unit
) {
    var countdown by remember { mutableStateOf(60) }
    var canResend by remember { mutableStateOf(false) }
    var codeError by remember { mutableStateOf<String?>(null) }

    // Countdown timer for resend button
    LaunchedEffect(countdown) {
        if (countdown > 0) {
            kotlinx.coroutines.delay(1000)
            countdown--
        } else {
            canResend = true
        }
    }

    // Reset countdown when code is resent
    LaunchedEffect(canResend) {
        // Reset handled by resend action
    }

    Div({ classes(AppStyleSheet.loginContainer) }) {
        Div({ classes(AppStyleSheet.loginForm) }) {
            H2({ classes(AppStyleSheet.loginTitle) }) {
                Text("メールアドレスを認証")
            }

            P({
                style {
                    property("color", DesignTokens.Colors.textSecondary.toString())
                    property("margin-bottom", "24px")
                    property("text-align", "center")
                }
            }) {
                Text("「$email」に送信された6桁のコードを入力してください。")
            }

            // Verification code input
            Div({ classes(AppStyleSheet.formGroup) }) {
                Label("code-input") {
                    Text("認証コード")
                }

                val inputClasses = buildList {
                    add(AppStyleSheet.textInput)
                    if (codeError != null || error != null) add(AppStyleSheet.inputInvalid)
                    else if (code.length == 6) add(AppStyleSheet.inputValid)
                }

                Input(type = InputType.Text) {
                    id("code-input")
                    classes(*inputClasses.toTypedArray())
                    value(code)
                    attr("autocomplete", "one-time-code")
                    attr("required", "true")
                    attr("maxlength", "6")
                    attr("pattern", "[0-9]{6}")
                    attr("inputmode", "numeric")
                    attr("aria-invalid", if (codeError != null || error != null) "true" else "false")
                    style {
                        property("font-size", "24px")
                        property("letter-spacing", "8px")
                        property("text-align", "center")
                        property("font-family", "monospace")
                    }
                    onInput {
                        val value = it.value.filter { c -> c.isDigit() }
                        if (value.length <= 6) {
                            onCodeChange(value)
                            codeError = null
                        }
                    }
                    if (isLoading) {
                        disabled()
                    }
                }

                // Code validation hint
                P({
                    id("code-hint")
                    classes(AppStyleSheet.inputHint)
                }) {
                    when {
                        code.length < 6 -> Text("6桁のコードを入力してください")
                        codeError != null -> Text(codeError!!)
                        error != null -> Text(error!!)
                        else -> Text("メールに記載されたコードを入力")
                    }
                }
            }

            // Error messages
            if (error != null && codeError == null) {
                val errorMsg = error!!
                Div({
                    classes(AppStyleSheet.formError)
                    attr("role", "alert")
                    attr("aria-live", "assertive")
                }) {
                    Text(errorMsg)
                }
            }

            // Buttons
            Div({ classes(AppStyleSheet.loginButtonGroup) }) {
                Button({
                    classes(AppStyleSheet.primaryButton)
                    if (code.length != 6 || isLoading) attr("disabled", "true")
                    onClick {
                        codeError = when {
                            code.isEmpty() -> "認証コードを入力してください"
                            code.length != 6 -> "認証コードは6桁です"
                            else -> null
                        }
                        if (codeError == null) {
                            onVerify()
                        }
                    }
                }) {
                    Text(if (isLoading) "認証中..." else "認証する")
                }

                Button({
                    classes(AppStyleSheet.secondaryButton)
                    if (!canResend || isLoading) attr("disabled", "true")
                    onClick {
                        if (canResend) {
                            onResend()
                            countdown = 60
                            canResend = false
                            onSendCode()
                        }
                    }
                }) {
                    Text(if (canResend) "再送信" else "再送信（${countdown}秒）")
                }

                Button({
                    classes(AppStyleSheet.secondaryButton)
                    onClick { onChangeEmail() }
                    if (isLoading) disabled()
                    style {
                        property("margin-top", "8px")
                    }
                }) {
                    Text("メールアドレスを変更")
                }
            }

            // Help text
            P({
                style {
                    property("margin-top", "16px")
                    property("font-size", "14px")
                    property("color", DesignTokens.Colors.textSecondary.toString())
                    property("text-align", "center")
                }
            }) {
                Text("メールが届かない場合は、迷惑メールフォルダをご確認ください。")
            }
        }
    }
}
